import { ChatAvatars } from "@app/components/assistant/conversation/ChatAvatars";
import {
  ChatRowSuffix,
  chatRowClassName,
} from "@app/components/assistant/conversation/ChatsList";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import { guideParticipantId } from "@app/lib/api/people/group_key";
import { useAppRouter } from "@app/lib/platform";
import { useOpenDirectChat } from "@app/lib/swr/chats";
import { getChatRoute } from "@app/lib/utils/router";
import { type ChatSummaryType, chatUnreadCap } from "@app/types/api/chats";
import { GUIDE_AVATAR_URL } from "@app/types/assistant/avatar";
import type { LightWorkspaceType } from "@app/types/user";
import { NavigationListItem, Spinner } from "@dust-tt/sparkle";
import { useContext, useState } from "react";

const guideParticipant = {
  sId: guideParticipantId,
  fullName: "Guide",
  image: GUIDE_AVATAR_URL,
  isActiveMember: true,
  kind: "agent" as const,
};

interface MemoairSupportChatsProps {
  owner: LightWorkspaceType;
  chats: ChatSummaryType[];
  activeChatId: string | null;
}

export function MemoairSupportChats({
  owner,
  chats,
  activeChatId,
}: MemoairSupportChatsProps) {
  const router = useAppRouter();
  const { sidebarOpen, setSidebarOpen } = useContext(SidebarContext);
  const openDirectChat = useOpenDirectChat({ owner });
  const [isOpeningGuide, setIsOpeningGuide] = useState(false);

  const foundersChat = chats.find((chat) => chat.kind === "support");
  const guideChat = chats.find((chat) => chat.kind === "direct");

  const closeSidebarBeforeNavigating = async () => {
    if (sidebarOpen) {
      setSidebarOpen(false);
      await new Promise((resolve) => setTimeout(resolve, 600));
    }
  };

  const renderChat = (chat: ChatSummaryType) => {
    const name = chat.title ?? chat.label;
    return (
      <NavigationListItem
        key={chat.sId}
        selected={chat.sId === activeChatId}
        label={name}
        description={
          chat.preview
            ? `${chat.preview.senderName}: ${chat.preview.text}`
            : undefined
        }
        href={getChatRoute(owner.sId, chat.sId)}
        shallow
        hasActivity={chat.unreadCount > 0}
        suffix={
          <ChatRowSuffix
            activityAt={chat.lastActivityAt}
            unreadCount={chat.unreadCount}
            unreadCap={chatUnreadCap}
            name={name}
          />
        }
        className={chatRowClassName}
        avatar={<ChatAvatars participants={chat.participants} />}
        onClick={closeSidebarBeforeNavigating}
      />
    );
  };

  // Guide's DM is opened on first click, so it reads empty until the user writes and Guide
  // introduces itself in its reply.
  const openGuide = async () => {
    if (isOpeningGuide) {
      return;
    }
    setIsOpeningGuide(true);
    await closeSidebarBeforeNavigating();
    const result = await openDirectChat([guideParticipantId]);
    setIsOpeningGuide(false);
    if (result.isOk()) {
      void router.push(getChatRoute(owner.sId, result.value.chatId));
    }
  };

  return (
    <>
      {foundersChat && renderChat(foundersChat)}
      {guideChat ? (
        renderChat(guideChat)
      ) : (
        <NavigationListItem
          label="Guide"
          description="How to use Memoair and what to set up next"
          suffix={isOpeningGuide ? <Spinner size="xs" /> : undefined}
          className={chatRowClassName}
          avatar={<ChatAvatars participants={[guideParticipant]} />}
          onClick={() => void openGuide()}
        />
      )}
    </>
  );
}
